<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import plural from 'plural-ru'
import dayjs from '@/lib/dayjs'
import { useScreen } from '@/composables/useScreen'
import weekdayPlugin from 'dayjs/plugin/weekday'
import EventCell from './EventCell.vue'
import type { WebinarCalendarEvent } from '../types'

const props = defineProps<{
  viewDate: Date
  currentDate: Date
  events: WebinarCalendarEvent[]
}>()

const emit = defineEmits<{
  (e: 'event-click', event: WebinarCalendarEvent): void
}>()

const { isMobile } = useScreen()
const container = ref<HTMLElement | null>(null)
const fittingEventsCount = ref(0)

dayjs.extend(weekdayPlugin)

watch(
  () => props.viewDate,
  async () => {
    await nextTick()
    calcFittingEventsCount()
  }
)

onMounted(() => {
  const cell = container.value?.querySelector('*:first-child') as HTMLElement | null
  cell?.addEventListener('resize', calcFittingEventsCount)
  calcFittingEventsCount()
})

onBeforeUnmount(() => {
  const cell = container.value?.querySelector('*:first-child') as HTMLElement | null
  cell?.removeEventListener('resize', calcFittingEventsCount)
})

function calcFittingEventsCount() {
  if (!container.value) return
  const cell = container.value.querySelector('*:first-child') as HTMLElement | null
  if (!cell) return
  const cellHeight = cell.getBoundingClientRect().height
  const reservedHeight = 24 + 12 + 4
  const minEventHeight = 50
  fittingEventsCount.value = Math.floor((cellHeight - reservedHeight) / minEventHeight)
}

const currentWeekday = computed(() => dayjs(props.currentDate).weekday())

const eventsByDate = computed(() => {
  const result: Record<string, WebinarCalendarEvent[]> = {}
  for (const event of props.events) {
    const key = dayjs(event.start_time).format('MM-DD')
    result[key] = [...(result[key] || []), event]
  }
  return result
})

const weekdays = computed(() => {
  const result: Array<{ label: string; isCurrent: boolean }> = []
  const weekdayFormat = isMobile.value ? 'dd' : 'dddd'
  for (let i = 0; i < 7; i++) {
    result.push({
      label: dayjs().weekday(i).format(weekdayFormat),
      isCurrent: currentWeekday.value === i
    })
  }
  return result
})

const days = computed(() => {
  const monthStart = dayjs(props.viewDate).startOf('month')
  const periodStart = dayjs(monthStart).weekday(0)
  const periodEnd = dayjs(props.viewDate).endOf('month').weekday(6)
  const result: Array<{
    label: string
    value: Date
    isOutOfRange: boolean
    isPast: boolean
    isToday: boolean
    menuTitle: string
    menuAlignment: 'left' | 'center' | 'right'
    events: WebinarCalendarEvent[]
  }> = []

  let date = dayjs(periodStart).startOf('day')
  while (date.isBefore(periodEnd)) {
    const key = date.format('MM-DD')
    const dayEvents = eventsByDate.value[key] || []

    let menuAlignment: 'left' | 'center' | 'right' = 'center'
    if (date.day() === 1) menuAlignment = 'left'
    if (date.day() === 0) menuAlignment = 'right'

    result.push({
      label: date.format('D'),
      value: date.toDate(),
      isOutOfRange: !date.isSame(monthStart, 'month'),
      isPast: date.isBefore(props.currentDate),
      isToday: date.isSame(props.currentDate, 'day'),
      menuTitle: date.format('dddd, DD MMMM'),
      menuAlignment,
      events: dayEvents
    })

    date = date.add(1, 'day')
  }

  return result
})

function capitalize(value: string) {
  if (!value) return ''
  return value.charAt(0).toUpperCase() + value.slice(1)
}

function pluralize(value = 0, one: string, two: string, five: string) {
  return `${value} ${plural(value, one, two, five)}`
}
</script>

<template>
  <div class="webinar-calendar__view webinar-calendar__view_month">
    <div class="webinar-calendar__month-header">
      <div
        v-for="(weekday, i) in weekdays"
        :key="i"
        class="webinar-calendar__month-weekday"
        :class="{ 'webinar-calendar__month-weekday_active': weekday.isCurrent }"
      >
        {{ capitalize(weekday.label) }}
      </div>
    </div>

    <div ref="container" class="webinar-calendar__month-container">
      <div
        v-for="day in days"
        :key="day.value.toJSON()"
        class="webinar-calendar__month-cell"
        :class="{
          'webinar-calendar__month-cell_disabled': day.isOutOfRange,
          'webinar-calendar__month-cell_today': day.isToday,
          'webinar-calendar__month-cell_past': day.isPast && !day.isToday
        }"
      >
        <div class="webinar-calendar__month-cell-label" v-text="day.label" />

        <template v-if="day.events.length <= fittingEventsCount">
          <EventCell
            v-for="event in day.events"
            :key="event.id"
            class="webinar-calendar__month-event-cell"
            :event="event"
            rounded
            show-time
            @event-click="e => emit('event-click', e)"
          />
        </template>

        <v-menu-base v-else pos-vertical="top" :pos-horizontal="day.menuAlignment" :min-height="200">
          <template #activator="{ onclick }">
            <div class="webinar-event-cell webinar-event-cell_has-more webinar-event-cell_rounded" @click="onclick">
              <div class="webinar-event-cell__content">
                <span class="webinar-event-cell__title">
                  Есть {{ pluralize(day.events.length, 'вебинар', 'вебинара', 'вебинаров') }}
                </span>

                <span class="webinar-event-cell__price green-text">
                  Показать <v-icon size="12">mdi-chevron-right</v-icon>
                </span>
              </div>
            </div>
          </template>

          <template #default="{ close }">
            <div class="webinar-calendar__month-events-menu">
              <div class="webinar-calendar__month-menu-title">
                {{ capitalize(day.menuTitle) }}
                <div class="webinar-calendar__month-menu-spacer"></div>
                <v-icon @click="close">mdi-close</v-icon>
              </div>

              <div class="webinar-calendar__month-all-events">
                <EventCell
                  v-for="event in day.events"
                  :key="event.id"
                  class="webinar-calendar__month-event-cell"
                  :event="event"
                  rounded
                  show-time
                  @event-click="e => emit('event-click', e)"
                />
              </div>
            </div>
          </template>
        </v-menu-base>
      </div>
    </div>
  </div>
</template>

<style lang="scss">
@use 'sass:color';

.webinar-calendar {
  &__view_month {
    display: flex;
    flex-direction: column;

    border: 1px rgba(217, 217, 217, 0.6) solid;
    border-left: none;
    border-top: none;
  }

  // Header
  &__month-header {
    display: grid;
    grid-template-columns: repeat(7, 1fr);
  }

  &__month-weekday {
    flex: 1 0 auto;
    padding: 12px 8px 4px;

    @include text-caption;

    border: 1px $lightGray solid;
    &:not(:last-child) {
      border-right: none;
    }

    &_active {
      padding: 10px 8px 4px;
      border-top: 2px $blue solid;
      color: $blue;
    }
  }

  // Content
  &__month-container {
    flex: 1;
    margin: -1px 0 0;

    display: grid;
    grid-template-columns: repeat(7, minmax(0, 1fr));
    grid-auto-rows: minmax(0, 1fr);
  }

  &__month-cell {
    border: 1px color.adjust($lightGray, $alpha: -0.4) solid;
    margin: 0 -1px -1px 0;

    // 24px date label at the top,
    // 12px hidden events count label
    // 4px margin between events
    // 50px one event cell height
    // 8px inner padding (top+bottom)
    min-height: 98px;

    padding: 4px;

    display: flex;
    flex-direction: column;

    &_disabled {
      color: $gray;
    }

    &_today {
      border-top: 3px $blue solid;
      color: $blue;
    }

    &_past {
      background: $lightenGray;
    }
  }

  &__month-event-cell:not(:last-child) {
    margin-bottom: 4px;
  }

  &__month-cell-label {
    @include text-caption;
    user-select: none;
    padding: 4px;
  }

  &__month-more-label {
    flex: 1;
    display: flex;
    flex-direction: column-reverse;

    font-size: 10px;
    font-weight: 400;
    line-height: 12px;

    color: $darkGray;
    cursor: pointer;
  }

  // All events menu
  &__month-events-menu {
    padding: 16px;
  }

  &__month-menu-title {
    margin-bottom: 16px;
    display: flex;
  }

  &__month-menu-spacer {
    flex: 1;
    min-width: 24px;
  }

  &__month-all-events {
    display: flex;
    flex-direction: column;
  }
}
</style>
